"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useSavedPaymentMethodInfo = void 0;
/*
Copyright (C) 2009 - 2021 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
const react_1 = require("react");
const lodash_1 = require("lodash");
const index_1 = require("./index");
/**
 * This hook sets up the state and info related to a payment form including saved payment methods and initial card values.
 */
const useSavedPaymentMethodInfo = (props) => {
    const { authState } = props;
    const { isAuthenticated, isAuthenticating } = authState;
    const { savedPaymentMethods: payments, isFetching: loadingSavedPaymentMethods } = (0, index_1.useListSavedPaymentMethods)(props);
    const defaultSavedPaymentMethod = useDefaultSavedPaymentMethod(payments);
    const [selectedSavedPaymentMethod, setSelectedSavedPaymentMethod] = (0, react_1.useState)(defaultSavedPaymentMethod || {});
    const initialCardValues = useInitialCardValues(selectedSavedPaymentMethod);
    (0, react_1.useEffect)(() => {
        if (isAuthenticating || loadingSavedPaymentMethods) {
            return;
        }
        if (!isAuthenticated || (0, lodash_1.isEmpty)(payments)) {
            setSelectedSavedPaymentMethod({});
            return;
        }
        setSelectedSavedPaymentMethod(defaultSavedPaymentMethod || {});
    }, [
        defaultSavedPaymentMethod,
        isAuthenticated,
        isAuthenticating,
        loadingSavedPaymentMethods,
        payments
    ]);
    return (0, react_1.useMemo)(() => ({
        initialCardValues,
        payments,
        selectedSavedPaymentMethod,
        setSelectedSavedPaymentMethod
    }), [initialCardValues, payments, selectedSavedPaymentMethod]);
};
exports.useSavedPaymentMethodInfo = useSavedPaymentMethodInfo;
const useDefaultSavedPaymentMethod = (savedPaymentMethods) => {
    const [savedPaymentMethod, setSavedPaymentMethod] = (0, react_1.useState)();
    (0, react_1.useEffect)(() => {
        setSavedPaymentMethod(savedPaymentMethods.filter(p => p.defaultForOwner)[0] ||
            (0, lodash_1.head)(savedPaymentMethods));
    }, [savedPaymentMethods]);
    return savedPaymentMethod;
};
const EmptyCardValues = {
    savedPaymentMethodId: '',
    cardNumber: '',
    nameOnAccount: '',
    cardType: '',
    expirationMonth: new Date().getMonth() + 1,
    expirationYear: new Date().getUTCFullYear(),
    creditCardSecCode: '',
    savePaymentForFutureUse: false,
    amountToApply: 0
};
const useInitialCardValues = (selectedPayment) => {
    const [values, setValues] = (0, react_1.useState)(EmptyCardValues);
    (0, react_1.useEffect)(() => {
        if ((0, lodash_1.isEmpty)(selectedPayment)) {
            setValues(EmptyCardValues);
            return;
        }
        const { creditCardHolder = '', creditCardNumber = '', creditCardType = '', creditCardExpDateYear = '', creditCardExpDateMonth = '' } = selectedPayment.displayAttributes || {};
        setValues({
            savedPaymentMethodId: selectedPayment.id || '',
            cardNumber: creditCardNumber,
            nameOnAccount: creditCardHolder,
            cardType: creditCardType,
            expirationMonth: creditCardExpDateMonth
                ? Number(creditCardExpDateMonth)
                : new Date().getMonth() + 1,
            expirationYear: creditCardExpDateYear
                ? Number(creditCardExpDateYear)
                : new Date().getUTCFullYear(),
            creditCardSecCode: '',
            savePaymentForFutureUse: false,
            amountToApply: 0
        });
    }, [selectedPayment]);
    return values;
};
//# sourceMappingURL=use-saved-payment-method-info.js.map